import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  BarChart3, Calendar, Download, FileText, DollarSign, 
  TrendingUp, Clock, Lock, ChevronDown, Printer,
  PieChart, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPage() {
  const { token, currentOutlet } = useAuth();
  const outletId = currentOutlet?.id;
  const [activeTab, setActiveTab] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  
  // Report data
  const [dailyReport, setDailyReport] = useState(null);
  const [daypartReport, setDaypartReport] = useState(null);
  const [categoryReport, setCategoryReport] = useState(null);
  const [cashReport, setCashReport] = useState(null);
  const [cogsReport, setCogsReport] = useState(null);

  const fetchDailyReport = useCallback(async () => {
    if (!outletId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/finance/daily-sales?outlet_id=${outletId}&business_date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDailyReport(await res.json());
      } else {
        toast.error('Failed to load daily report');
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [outletId, token, selectedDate]);

  const fetchDaypartReport = useCallback(async () => {
    if (!outletId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/finance/daypart-sales?outlet_id=${outletId}&business_date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDaypartReport(await res.json());
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [outletId, token, selectedDate]);

  const fetchCategoryReport = useCallback(async () => {
    if (!outletId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/finance/category-sales?outlet_id=${outletId}&start_date=${startDate}&end_date=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCategoryReport(await res.json());
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [outletId, token, startDate, endDate]);

  const fetchCogsReport = useCallback(async () => {
    if (!outletId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/cogs?outlet_id=${outletId}&start_date=${startDate}&end_date=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCogsReport(await res.json());
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [outletId, token, startDate, endDate]);

  const fetchCashReport = useCallback(async () => {
    if (!outletId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/finance/cash-summary?outlet_id=${outletId}&business_date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCashReport(await res.json());
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [outletId, token, selectedDate]);

  useEffect(() => {
    if (activeTab === 'daily') fetchDailyReport();
    if (activeTab === 'daypart') fetchDaypartReport();
    if (activeTab === 'category') fetchCategoryReport();
    if (activeTab === 'cash') fetchCashReport();
    if (activeTab === 'cogs') fetchCogsReport();
  }, [activeTab, fetchDailyReport, fetchDaypartReport, fetchCategoryReport, fetchCashReport, fetchCogsReport]);

  const handleExport = async (type) => {
    if (!outletId || !token) return;
    
    let url = '';
    let filename = '';
    
    switch (type) {
      case 'daily':
        url = `${API_URL}/api/finance/export/daily-sales?outlet_id=${outletId}&start_date=${startDate}&end_date=${endDate}`;
        filename = `daily_sales_${startDate}_${endDate}.csv`;
        break;
      case 'category':
        url = `${API_URL}/api/finance/export/category-sales?outlet_id=${outletId}&start_date=${startDate}&end_date=${endDate}`;
        filename = `category_sales_${startDate}_${endDate}.csv`;
        break;
      case 'cash':
        url = `${API_URL}/api/finance/export/cash-summary?outlet_id=${outletId}&business_date=${selectedDate}`;
        filename = `cash_summary_${selectedDate}.csv`;
        break;
      default:
        return;
    }
    
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        toast.success('Export downloaded');
      } else {
        toast.error('Export failed');
      }
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const formatCurrency = (amount) => `₱${(amount || 0).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="reports-page">
      {/* Header */}
      <header className="h-14 bg-card border-b border-border flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-teal-400" />
          <h1 className="text-xl font-semibold">Finance Reports</h1>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="w-40 bg-muted border-border"
            data-testid="report-date-picker"
          />
        </div>
      </header>

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-card">
            <TabsTrigger value="daily" className="data-[state=active]:bg-teal-600">
              <FileText className="w-4 h-4 mr-2" />Daily Sales
            </TabsTrigger>
            <TabsTrigger value="daypart" className="data-[state=active]:bg-teal-600">
              <Clock className="w-4 h-4 mr-2" />Daypart
            </TabsTrigger>
            <TabsTrigger value="category" className="data-[state=active]:bg-teal-600">
              <PieChart className="w-4 h-4 mr-2" />Category
            </TabsTrigger>
            <TabsTrigger value="cogs" className="data-[state=active]:bg-teal-600">
              <PieChart className="w-4 h-4 mr-2" />COGS
            </TabsTrigger>
            <TabsTrigger value="cash" className="data-[state=active]:bg-teal-600">
              <DollarSign className="w-4 h-4 mr-2" />Cash Summary
            </TabsTrigger>
          </TabsList>

          {/* Daily Sales Report */}
          <TabsContent value="daily" className="space-y-4">
            {dailyReport && (
              <>
                {/* Z-Lock Badge */}
                {dailyReport.is_locked && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-red-900/50 border border-red-700 rounded-lg" data-testid="z-locked-badge">
                    <Lock className="w-4 h-4 text-red-400" />
                    <span className="text-red-300 text-sm">Z-Locked: {dailyReport.z_number}</span>
                  </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <SummaryCard title="Total Sales" value={formatCurrency(dailyReport.total_sales)} icon={<TrendingUp />} color="teal" />
                  <SummaryCard title="Net Sales" value={formatCurrency(dailyReport.net_sales)} icon={<DollarSign />} color="green" />
                  <SummaryCard title="Transactions" value={dailyReport.transaction_count} icon={<FileText />} color="blue" />
                  <SummaryCard title="VAT" value={formatCurrency(dailyReport.vat_amount)} icon={<BarChart3 />} color="purple" />
                </div>

                {/* Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Payment Breakdown */}
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <h3 className="text-lg font-medium mb-4">Payment Breakdown</h3>
                    <div className="space-y-3">
                      <PaymentRow label="Cash" amount={dailyReport.payment_breakdown?.cash} total={dailyReport.total_sales} />
                      <PaymentRow label="Card" amount={dailyReport.payment_breakdown?.card} total={dailyReport.total_sales} />
                      <PaymentRow label="QR/Other" amount={(dailyReport.payment_breakdown?.qr || 0) + (dailyReport.payment_breakdown?.other || 0)} total={dailyReport.total_sales} />
                    </div>
                  </div>

                  {/* Adjustments */}
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <h3 className="text-lg font-medium mb-4">Adjustments</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Service Charge</span>
                        <span className="text-green-400">+{formatCurrency(dailyReport.service_charge)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Discounts</span>
                        <span className="text-red-400">-{formatCurrency(dailyReport.discounts_total)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Refunds ({dailyReport.refund_count})</span>
                        <span className="text-red-400">-{formatCurrency(dailyReport.refunds_total)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Voids ({dailyReport.void_count})</span>
                        <span className="text-orange-400">{formatCurrency(dailyReport.voids_total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shift Summaries */}
                {dailyReport.shift_summaries?.length > 0 && (
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <h3 className="text-lg font-medium mb-4">Shift Summaries</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead>Started</TableHead>
                          <TableHead>Ended</TableHead>
                          <TableHead className="text-right">Starting Cash</TableHead>
                          <TableHead className="text-right">Sales</TableHead>
                          <TableHead className="text-right">Transactions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dailyReport.shift_summaries.map((shift, i) => (
                          <TableRow key={i} className="border-border">
                            <TableCell>{shift.started_at ? new Date(shift.started_at).toLocaleTimeString() : '-'}</TableCell>
                            <TableCell>{shift.ended_at ? new Date(shift.ended_at).toLocaleTimeString() : 'Active'}</TableCell>
                            <TableCell className="text-right">{formatCurrency(shift.starting_cash)}</TableCell>
                            <TableCell className="text-right text-teal-400">{formatCurrency(shift.sales)}</TableCell>
                            <TableCell className="text-right">{shift.transactions}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Export */}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => handleExport('daily')} data-testid="export-daily-csv">
                    <Download className="w-4 h-4 mr-2" />Export CSV
                  </Button>
                  <Button variant="outline" onClick={() => window.print()}>
                    <Printer className="w-4 h-4 mr-2" />Print
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* Daypart Report */}
          <TabsContent value="daypart" className="space-y-4">
            {daypartReport && (
              <>
                {daypartReport.is_locked && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-red-900/50 border border-red-700 rounded-lg">
                    <Lock className="w-4 h-4 text-red-400" />
                    <span className="text-red-300 text-sm">Z-Locked</span>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {daypartReport.dayparts?.map(dp => (
                    <div key={dp.daypart} className="bg-card rounded-lg p-4 border border-border">
                      <h4 className="text-sm text-muted-foreground mb-1">{dp.label}</h4>
                      <p className="text-2xl font-bold text-teal-400">{formatCurrency(dp.sales)}</p>
                      <p className="text-sm text-muted-foreground">{dp.transactions} transactions</p>
                      <p className="text-xs text-muted-foreground">Avg: {formatCurrency(dp.avg_ticket)}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-card rounded-lg p-4 border border-border">
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <span className="text-muted-foreground">Total</span>
                    <span className="text-xl font-bold text-teal-400">{formatCurrency(daypartReport.total_sales)}</span>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Category Report */}
          <TabsContent value="category" className="space-y-4">
            <div className="flex gap-4 mb-4">
              <div>
                <label className="text-sm text-muted-foreground">Start Date</label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                       className="bg-muted border-border" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">End Date</label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                       className="bg-muted border-border" />
              </div>
              <div className="self-end">
                <Button onClick={fetchCategoryReport} className="bg-teal-600">Apply</Button>
              </div>
            </div>

            {categoryReport && (
              <>
                <div className="bg-card rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Qty Sold</TableHead>
                        <TableHead>Top Items</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryReport.categories?.map(cat => (
                        <TableRow key={cat.category_id} className="border-border">
                          <TableCell className="font-medium">{cat.category_name}</TableCell>
                          <TableCell className="text-right text-teal-400">{formatCurrency(cat.revenue)}</TableCell>
                          <TableCell className="text-right">{cat.quantity_sold}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {cat.top_items?.slice(0, 2).map(i => i.item_name).join(', ')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-between items-center bg-card rounded-lg p-4 border border-border">
                  <span className="font-medium">Total Revenue</span>
                  <span className="text-xl font-bold text-teal-400">{formatCurrency(categoryReport.total_revenue)}</span>
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => handleExport('category')} data-testid="export-category-csv">
                    <Download className="w-4 h-4 mr-2" />Export CSV
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* Cash Summary Report */}
          <TabsContent value="cogs" className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40 bg-muted border-border" data-testid="cogs-start" />
              <span className="text-muted-foreground">to</span>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40 bg-muted border-border" data-testid="cogs-end" />
              <Button onClick={fetchCogsReport} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                {loading ? 'Loading…' : 'Run'}
              </Button>
            </div>

            {cogsReport && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    ['Net Sales', formatCurrency(cogsReport.total_sales), 'text-foreground'],
                    ['Cost of Goods (FIFO)', formatCurrency(cogsReport.total_cogs), 'text-orange-400'],
                    ['Gross Profit', formatCurrency(cogsReport.gross_profit), 'text-teal-400'],
                    ['Gross Margin', cogsReport.gross_margin_percent != null ? `${cogsReport.gross_margin_percent}%` : '—', 'text-teal-400'],
                  ].map(([label, val, cls]) => (
                    <div key={label} className="bg-card border border-border rounded-lg p-4">
                      <p className="text-xs text-muted-foreground mb-1">{label}</p>
                      <p className={`text-xl font-bold ${cls}`}>{val}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  COGS is the real FIFO cost booked on each sold line (oldest stock first). Sales here is product net of line discounts and excludes service charge; only items with a recipe contribute cost.
                </p>

                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="px-4 py-2 border-b border-border text-sm font-medium text-muted-foreground">By Category</div>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Category</TableHead><TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Sales</TableHead><TableHead className="text-right">COGS</TableHead>
                      <TableHead className="text-right">Gross Profit</TableHead><TableHead className="text-right">Margin</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(cogsReport.by_category || []).map((c, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{c.category}</TableCell>
                          <TableCell className="text-right">{c.qty}</TableCell>
                          <TableCell className="text-right">{formatCurrency(c.sales)}</TableCell>
                          <TableCell className="text-right text-orange-400">{formatCurrency(c.cogs)}</TableCell>
                          <TableCell className="text-right text-teal-400">{formatCurrency(c.gross_profit)}</TableCell>
                          <TableCell className="text-right">{c.margin_percent != null ? `${c.margin_percent}%` : '—'}</TableCell>
                        </TableRow>
                      ))}
                      {!(cogsReport.by_category || []).length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No sales in range</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>

                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="px-4 py-2 border-b border-border text-sm font-medium text-muted-foreground">By Item (highest cost first)</div>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Item</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Sales</TableHead><TableHead className="text-right">COGS</TableHead><TableHead className="text-right">Margin</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(cogsReport.by_item || []).slice(0, 50).map((it, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{it.name}</TableCell>
                          <TableCell className="text-muted-foreground">{it.category}</TableCell>
                          <TableCell className="text-right">{it.qty}</TableCell>
                          <TableCell className="text-right">{formatCurrency(it.sales)}</TableCell>
                          <TableCell className="text-right text-orange-400">{formatCurrency(it.cogs)}</TableCell>
                          <TableCell className="text-right">{it.margin_percent != null ? `${it.margin_percent}%` : '—'}</TableCell>
                        </TableRow>
                      ))}
                      {!(cogsReport.by_item || []).length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No sales in range</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="cash" className="space-y-4">
            {cashReport && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <SummaryCard title="Starting Cash" value={formatCurrency(cashReport.starting_cash)} icon={<DollarSign />} color="blue" />
                  <SummaryCard title="Cash Sales" value={formatCurrency(cashReport.cash_sales)} icon={<ArrowUpRight />} color="green" />
                  <SummaryCard title="Cash In/Out" value={formatCurrency(cashReport.cash_in_total - cashReport.cash_out_total)} icon={<TrendingUp />} color="yellow" />
                  <SummaryCard title="Expected" value={formatCurrency(cashReport.expected_balance)} icon={<DollarSign />} color="teal" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <h3 className="text-lg font-medium mb-4">Cash Flow</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Starting Cash</span>
                        <span>{formatCurrency(cashReport.starting_cash)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cash Sales</span>
                        <span className="text-green-400">+{formatCurrency(cashReport.cash_sales)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cash In</span>
                        <span className="text-green-400">+{formatCurrency(cashReport.cash_in_total)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cash Out</span>
                        <span className="text-red-400">-{formatCurrency(cashReport.cash_out_total)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Deposits</span>
                        <span className="text-blue-400">-{formatCurrency(cashReport.deposits_total)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Refunds</span>
                        <span className="text-red-400">-{formatCurrency(cashReport.refunds_total)}</span>
                      </div>
                      <div className="flex justify-between border-t border-border pt-2 mt-2">
                        <span className="font-medium">Expected Balance</span>
                        <span className="font-bold text-teal-400">{formatCurrency(cashReport.expected_balance)}</span>
                      </div>
                      {cashReport.counted_cash !== null && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Counted Cash</span>
                            <span>{formatCurrency(cashReport.counted_cash)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Variance</span>
                            <span className={cashReport.variance >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {formatCurrency(cashReport.variance)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Movements */}
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <h3 className="text-lg font-medium mb-4">Recent Movements</h3>
                    <ScrollArea className="h-64">
                      <div className="space-y-2">
                        {cashReport.movements?.slice(-10).reverse().map((m, i) => (
                          <div key={i} className="flex justify-between text-sm py-1 border-b border-border">
                            <div>
                              <span className="text-muted-foreground capitalize">{m.type.replace('_', ' ')}</span>
                              {m.description && <p className="text-xs text-muted-foreground">{m.description}</p>}
                            </div>
                            <span className={m.amount >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {m.amount >= 0 ? '+' : ''}{formatCurrency(m.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => handleExport('cash')} data-testid="export-cash-csv">
                    <Download className="w-4 h-4 mr-2" />Export CSV
                  </Button>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, icon, color }) {
  const colors = {
    teal: 'text-teal-400 bg-teal-500/10',
    green: 'text-green-400 bg-green-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    yellow: 'text-yellow-400 bg-yellow-500/10',
  };
  
  return (
    <div className="bg-card rounded-lg p-4 border border-border">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">{title}</span>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          {React.cloneElement(icon, { className: 'w-4 h-4' })}
        </div>
      </div>
      <p className={`text-2xl font-bold ${colors[color].split(' ')[0]}`}>{value}</p>
    </div>
  );
}

function PaymentRow({ label, amount, total }) {
  const pct = total > 0 ? (amount / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span>₱{(amount || 0).toFixed(2)} ({pct.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
